import type {
  ImportJobFieldsFragment,
  ImportPersonActionFieldsFragment,
  ImportUnitFieldsFragment,
} from "@/graphql/generated/operations";
import {
  FileImportJobStatus,
  ImportJobStatus,
  ImportPersonAction,
  ImportPersonWarningCode,
  ImportUnitStatus,
} from "@/graphql/generated/types";

export function isTerminal(status: ImportJobStatus | undefined) {
  return status === ImportJobStatus.Completed || status === ImportJobStatus.Failed;
}

export function isFileJobTerminal(status: FileImportJobStatus | undefined) {
  return status === FileImportJobStatus.Completed || status === FileImportJobStatus.Failed;
}

interface MonotonicProgress {
  total: number;
  done: number;
  failed: number;
}

/**
 * Batch totals grow as Genesis accepts files, while done/failed grow as workers
 * finish. A subscription snapshot is at least as fresh as a queried one only
 * when none of those server-owned counters would move backwards.
 */
export function isProgressAtLeastAsFresh(
  candidate: MonotonicProgress | null | undefined,
  reference: MonotonicProgress | null | undefined,
) {
  if (!candidate) return false;
  if (!reference) return true;
  return (
    candidate.total >= reference.total &&
    candidate.done >= reference.done &&
    candidate.failed >= reference.failed
  );
}

/**
 * Splits the per-unit outcomes into the three buckets the result view shows
 * separately. Only meaningful in a terminal state: genesis leaves `units` empty
 * while a job runs, so a running job yields three empty buckets rather than a
 * half-truth assembled from the counters.
 */
export function partitionImportUnits(units: readonly ImportUnitFieldsFragment[]) {
  return {
    succeeded: units.filter((unit) => unit.status === ImportUnitStatus.Succeeded),
    failed: units.filter((unit) => unit.status === ImportUnitStatus.Failed),
    skipped: units.filter((unit) => unit.status === ImportUnitStatus.SkippedDependency),
  };
}

/**
 * Whether the preview contains at least one person whose stored identity and
 * incoming identity both disagree.
 *
 * This is deliberately only a *question*, never a verdict: genesis reports the
 * fact because a legitimate rename and a UUID pasted onto the wrong row are
 * indistinguishable from the backend. Spark therefore blocks the promotion of
 * such a preview into a real import until an admin confirms it by hand.
 */
export function actionsWithIdentityMismatch(
  personActions: readonly ImportPersonActionFieldsFragment[],
): ImportPersonActionFieldsFragment[] {
  return personActions.filter((action) =>
    action.warnings.includes(ImportPersonWarningCode.PersonIdentityMismatch),
  );
}

/** Person rows Genesis resolved as an explicit reactivation of a deleted profile. */
export function actionsThatRestoreDeletedPerson(
  personActions: readonly ImportPersonActionFieldsFragment[],
): ImportPersonActionFieldsFragment[] {
  return personActions.filter((action) => action.action === ImportPersonAction.Restore);
}

/**
 * Picks the freshest snapshot of `currentJobId` across the mutation response,
 * the status query, and the live subscription: all three can independently
 * hold data. A candidate whose `id` doesn't match `currentJobId` is dropped
 * outright, before any timestamp comparison: without that guard, a query or
 * subscription observable that hasn't yet caught up to a variables change
 * (e.g. right after "New import" starts a fresh job) could still be carrying
 * the *previous* job's data, and a later `updatedAt` on that stale snapshot
 * would otherwise win the comparison and render the wrong job.
 */
export function selectLatestImportJob(
  currentJobId: string | null | undefined,
  ...snapshots: Array<ImportJobFieldsFragment | null | undefined>
): ImportJobFieldsFragment | null {
  return snapshots.reduce<ImportJobFieldsFragment | null>((latest, candidate) => {
    if (!candidate || !currentJobId || candidate.id !== currentJobId) return latest;
    if (!latest) return candidate;
    const candidateUpdatedAt = Date.parse(String(candidate.updatedAt));
    const latestUpdatedAt = Date.parse(String(latest.updatedAt));
    if (candidateUpdatedAt > latestUpdatedAt) return candidate;
    if (candidateUpdatedAt === latestUpdatedAt && isTerminal(candidate.status) && !isTerminal(latest.status)) {
      return candidate;
    }
    return latest;
  }, null);
}

export type ImportPipelineStepId = "file" | "queue" | "import" | "result";
export type ImportPipelineStepState = "pending" | "active" | "done" | "failed";

export interface ImportPipelineStep {
  id: ImportPipelineStepId;
  state: ImportPipelineStepState;
}

const STEP_IDS: readonly ImportPipelineStepId[] = ["file", "queue", "import", "result"];

/**
 * Derives the compact File -> Queue -> Import -> Result summary shown above
 * the import status. Deliberately coarse: this is NOT a per-document
 * pipeline. INDEXING and IMPORTING both render as the single "import" node,
 * and a FAILED job always shows file + queue as done. That's not a guess:
 * `startImportDocuments` only ever returns a job after the file was read and
 * queued, so any failure is attributable to the import step itself.
 */
type PipelinePhase = "queued" | "running" | "completed" | "failed" | null;

function stepsForPhase(phase: PipelinePhase): ImportPipelineStep[] {
  switch (phase) {
    case null:
      return STEP_IDS.map((id): ImportPipelineStep => ({ id, state: id === "file" ? "active" : "pending" }));
    case "queued":
      return [
        { id: "file", state: "done" },
        { id: "queue", state: "active" },
        { id: "import", state: "pending" },
        { id: "result", state: "pending" },
      ];
    case "completed":
      return [
        { id: "file", state: "done" },
        { id: "queue", state: "done" },
        { id: "import", state: "done" },
        { id: "result", state: "done" },
      ];
    case "failed":
      return [
        { id: "file", state: "done" },
        { id: "queue", state: "done" },
        { id: "import", state: "failed" },
        { id: "result", state: "failed" },
      ];
    case "running":
    default:
      // stage is INDEXING or IMPORTING, both read as "import in progress".
      return [
        { id: "file", state: "done" },
        { id: "queue", state: "done" },
        { id: "import", state: "active" },
        { id: "result", state: "pending" },
      ];
  }
}

export function buildImportPipelineSteps(job: ImportJobFieldsFragment | null): ImportPipelineStep[] {
  if (!job) return stepsForPhase(null);
  switch (job.status) {
    case ImportJobStatus.Queued:
      return stepsForPhase("queued");
    case ImportJobStatus.Completed:
      return stepsForPhase("completed");
    case ImportJobStatus.Failed:
      return stepsForPhase("failed");
    case ImportJobStatus.Running:
    default:
      return stepsForPhase("running");
  }
}

/**
 * Same four-node summary for the route-independent job. Used while the shared
 * drop has only handed back a `FileImportJob` (right after the start, and for
 * the whole extraction route, which has no richer job of its own).
 */
export function buildFileImportPipelineSteps(status: FileImportJobStatus | undefined): ImportPipelineStep[] {
  switch (status) {
    case FileImportJobStatus.Queued:
      return stepsForPhase("queued");
    case FileImportJobStatus.Completed:
      return stepsForPhase("completed");
    case FileImportJobStatus.Failed:
      return stepsForPhase("failed");
    case FileImportJobStatus.Running:
      return stepsForPhase("running");
    default:
      return stepsForPhase(null);
  }
}
